import { useState, useCallback, useEffect, useRef } from "react";
import { supabase } from "../../../supabaseClient";

export const useCalculationEngine = (
  tournamentId,
  initialCalculationResults = {}
) => {
  // Calculation state - initialize with provided results
  const [calculationResults, setCalculationResults] = useState(
    initialCalculationResults
  );
  const [calculationErrors, setCalculationErrors] = useState({});
  const [calculatingTotals, setCalculatingTotals] = useState(new Set());
  const [calculatingTeams, setCalculatingTeams] = useState(new Set());

  // Queue for pending calculations - stores a function that returns the latest teamData
  // Using ref so we can access current value in callbacks without dependencies
  // This ensures we always use the most current data when processing the queue
  const pendingCalculationsRef = useRef(new Map());

  // Ref to store performCalculation function to break circular dependency
  const performCalculationRef = useRef(null);

  // Update calculation results when initial results change
  useEffect(() => {
    if (
      initialCalculationResults &&
      Object.keys(initialCalculationResults).length > 0
    ) {
      setCalculationResults(initialCalculationResults);
    }
  }, [initialCalculationResults]);

  // Get calculation results from cache
  const getCalculationResult = useCallback(
    (teamId, bowlerId, calculationType) => {
      const cacheKey = `${teamId}-${bowlerId}-${calculationType}`;
      return calculationResults[cacheKey] || null;
    },
    [calculationResults]
  );

  // Get calculation errors from cache
  const getCalculationError = useCallback(
    (teamId, bowlerId, calculationType) => {
      const cacheKey = `${teamId}-${bowlerId}-${calculationType}`;
      return calculationErrors[cacheKey] || null;
    },
    [calculationErrors]
  );

  // Clear calculations for a team
  const clearCalculations = useCallback((teamId, bowlerId = null) => {
    if (bowlerId) {
      // Clear specific bowler calculations
      const scratchKey = `${teamId}-${bowlerId}-scratch`;
      const handicapKey = `${teamId}-${bowlerId}-handicap`;

      setCalculationResults((prev) => {
        const newResults = { ...prev };
        delete newResults[scratchKey];
        delete newResults[handicapKey];
        return newResults;
      });

      setCalculationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[scratchKey];
        delete newErrors[handicapKey];
        return newErrors;
      });
    } else {
      // Clear all calculations for the team
      setCalculationResults((prev) => {
        const newResults = {};
        Object.keys(prev).forEach((key) => {
          if (!key.startsWith(`${teamId}-`)) {
            newResults[key] = prev[key];
          }
        });
        return newResults;
      });

      setCalculationErrors((prev) => {
        const newErrors = {};
        Object.keys(prev).forEach((key) => {
          if (!key.startsWith(`${teamId}-`)) {
            newErrors[key] = prev[key];
          }
        });
        return newErrors;
      });
    }
  }, []);

  // Internal function to actually perform the calculation
  const performCalculation = useCallback(
    async (teamId, teamData) => {
      setCalculatingTotals((prev) => new Set(prev).add(teamId));
      setCalculatingTeams((prev) => new Set(prev).add(teamId));

      // Safety timeout - clear state if calculation takes too long (10 seconds)
      const safetyTimeout = setTimeout(() => {
        setCalculatingTotals((prev) => {
          const newSet = new Set(prev);
          newSet.delete(teamId);
          return newSet;
        });
        setCalculatingTeams((prev) => {
          const newSet = new Set(prev);
          newSet.delete(teamId);
          return newSet;
        });
      }, 10000);

      try {
        // Prepare score data for API
        const currentTeamScores = {};

        if (teamData?.bowlers && Array.isArray(teamData.bowlers)) {
          teamData.bowlers.forEach((bowler) => {
            let bowlerScores = teamData.transformedScores?.[bowler.id] || [];

            // Initialize scores if empty
            if (bowlerScores.length === 0 && teamData.scores) {
              bowlerScores = Array.from(
                { length: teamData.gamesPerEvent || 3 },
                (_, i) => {
                  const existingScore = teamData.scores.find(
                    (s) => s.bowlerID === bowler.id && s.gameNumber === i + 1
                  );
                  return {
                    id: existingScore?.id || `${bowler.id}-${i}`,
                    gameNumber: i + 1,
                    score: existingScore?.score || null,
                    bowlerID: bowler.id,
                    teamID: teamData.id,
                  };
                }
              );
            }

            // Convert to array format for API
            const scoresArray = [];
            for (let i = 0; i < (teamData.gamesPerEvent || 3); i++) {
              if (
                bowlerScores[i] &&
                bowlerScores[i].score !== null &&
                bowlerScores[i].score !== undefined
              ) {
                const scoreValue =
                  typeof bowlerScores[i].score === "object"
                    ? bowlerScores[i].score.score
                    : bowlerScores[i].score;
                scoresArray[i] = scoreValue;
              } else {
                scoresArray[i] = 0;
              }
            }

            currentTeamScores[bowler.id] = scoresArray;
          });
        }

        // Call calculation API
        const { data: calculatedData, error: calcError } =
          await supabase.functions.invoke("calculate-scores", {
            body: {
              mode: "batch",
              tournamentId: tournamentId,
              requests: [
                {
                  calculationType: "team_scratch",
                  teamId: teamId,
                  eventId: teamData.eventsID,
                  scores: currentTeamScores,
                  gamesPerEvent: teamData.gamesPerEvent,
                },
                {
                  calculationType: "team_handicap",
                  teamId: teamId,
                  eventId: teamData.eventsID,
                  scores: currentTeamScores,
                  gamesPerEvent: teamData.gamesPerEvent,
                },
              ],
            },
          });

        if (calcError) {
          console.error("Error calling calculate-scores:", calcError);
          return;
        }

        // Update cache with results
        if (calculatedData?.results) {
          const results = calculatedData.results;
          const scratchResult = results.find(
            (r) => r.result?.type === "team_scratch"
          )?.result;
          const handicapResult = results.find(
            (r) => r.result?.type === "team_handicap"
          )?.result;

          if (scratchResult || handicapResult) {
            const cacheUpdates = {};

            // Store team totals
            if (scratchResult) {
              cacheUpdates[`${teamId}-team-scratch`] = scratchResult;
            }
            if (handicapResult) {
              cacheUpdates[`${teamId}-team-handicap`] = handicapResult;
            }

            // Store bowler totals
            if (handicapResult?.bowlerTotals) {
              handicapResult.bowlerTotals.forEach((bowlerTotal) => {
                if (bowlerTotal.bowlerId) {
                  const bowlerScratchKey = `${teamId}-${bowlerTotal.bowlerId}-scratch`;
                  const bowlerHandicapKey = `${teamId}-${bowlerTotal.bowlerId}-handicap`;

                  // Read existing values from current state (will be merged in state updater)
                  cacheUpdates[bowlerScratchKey] = {
                    total: bowlerTotal.scratchTotal || 0,
                    scratchTotal: bowlerTotal.scratchTotal || 0,
                  };
                  cacheUpdates[bowlerHandicapKey] = {
                    handicapTotal: bowlerTotal.handicapTotal || 0,
                    handicap: bowlerTotal.handicap || 0,
                  };
                }
              });
            }

            // Apply all cache updates - merge with existing results
            setCalculationResults((prev) => {
              const merged = { ...prev };
              // Merge each cache update, preserving existing properties
              Object.keys(cacheUpdates).forEach((key) => {
                merged[key] = {
                  ...(prev[key] || {}),
                  ...cacheUpdates[key],
                };
              });
              return merged;
            });
          }
        }
      } catch (error) {
        console.error("Error in performCalculation:", error);
      } finally {
        // Clear safety timeout
        clearTimeout(safetyTimeout);

        // Clear calculating state
        setCalculatingTotals((prev) => {
          const newSet = new Set(prev);
          newSet.delete(teamId);
          return newSet;
        });
        setCalculatingTeams((prev) => {
          const newSet = new Set(prev);
          newSet.delete(teamId);
          return newSet;
        });

        // Check queue after state is cleared using state updater function
        // This is deterministic - React guarantees the updater runs with latest state
        // and after any queued state updates from the same render cycle
        setCalculatingTeams((prev) => {
          const newSet = new Set(prev);
          newSet.delete(teamId);
          // Process queue after state update is applied
          // Use setTimeout(0) to schedule after current execution context
          // This ensures any queued requests from the same cycle are stored first
          setTimeout(() => {
            const pendingDataGetter =
              pendingCalculationsRef.current.get(teamId);
            if (pendingDataGetter) {
              pendingCalculationsRef.current.delete(teamId);
              const latestTeamData = pendingDataGetter();
              performCalculationRef
                .current?.(teamId, latestTeamData)
                .catch((error) => {
                  console.error("Error in queued calculation:", error);
                  setCalculatingTotals((prev) => {
                    const newSet = new Set(prev);
                    newSet.delete(teamId);
                    return newSet;
                  });
                  setCalculatingTeams((prev) => {
                    const newSet = new Set(prev);
                    newSet.delete(teamId);
                    return newSet;
                  });
                });
            }
          }, 0);
          return newSet;
        });
      }
    },
    [tournamentId]
  );

  // Store performCalculation in ref for use in queue processing
  performCalculationRef.current = performCalculation;

  // Public function that handles queueing
  const calculateTeamScores = useCallback(
    async (teamId, teamData, getLatestTeamData = null) => {
      // If already calculating, queue this request (overwrites any previous queued request)
      if (calculatingTeams.has(teamId)) {
        // Store a function that returns the latest teamData
        // If getLatestTeamData is provided, use it; otherwise use a function that returns current teamData
        const dataGetter = getLatestTeamData || (() => teamData);
        pendingCalculationsRef.current.set(teamId, dataGetter);

        // Check if calculation just finished (race condition: calculation might have just completed)
        // Use state updater to check current state deterministically
        setCalculatingTeams((prev) => {
          if (!prev.has(teamId)) {
            // Calculation already finished, process queue immediately
            // Use setTimeout(0) to schedule after current execution context
            // This ensures we don't interfere with any ongoing state updates
            setTimeout(() => {
              const queuedDataGetter =
                pendingCalculationsRef.current.get(teamId);
              if (queuedDataGetter) {
                pendingCalculationsRef.current.delete(teamId);
                const latestTeamData = queuedDataGetter();
                performCalculationRef
                  .current?.(teamId, latestTeamData)
                  .catch((error) => {
                    console.error("Error in queued calculation:", error);
                    setCalculatingTotals((prev) => {
                      const newSet = new Set(prev);
                      newSet.delete(teamId);
                      return newSet;
                    });
                    setCalculatingTeams((prev) => {
                      const newSet = new Set(prev);
                      newSet.delete(teamId);
                      return newSet;
                    });
                  });
              }
            }, 0);
          }
          return prev;
        });
        return;
      }

      // Not calculating, start immediately
      performCalculation(teamId, teamData);
    },
    [calculatingTeams, performCalculation]
  );

  return {
    calculatingTotals,
    getCalculationResult,
    getCalculationError,
    clearCalculations,
    calculateTeamScores,
  };
};
